import { Divider, Grid, Typography } from '@mui/material'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';

import style from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import popupStyles from "../../../assets/styles/componentStyles/globalPopupStyles.module.scss";

import { GlobalTable } from '../../utility/globalTable';
import { archiveContactListErrorDetails } from '../../../models/axiosModel';
import { HeaderListObjectModel } from '../../../models/componentModel';

interface ArchiveContactErrorPopupProps {
    errorDetails: archiveContactListErrorDetails | null,
}

interface archiveErrorTableRow {
    name: string,
    type: string,
    details: string,
    redirectUrl: string,
}

// Replaces underscores with spaces and capitalizes the first character.
// Example: "remove_contact" → "Remove contact"
const getWorkflowUsageLabel = (usageType: string) => {
    if (!usageType) return "-";
    return usageType.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function ArchiveContactErrorPopup(props: ArchiveContactErrorPopupProps) {

    const { errorDetails } = props;

    const campaigns = errorDetails?.campaigns ?? [];
    const workflows = errorDetails?.workflows ?? [];
    const customRecipients = errorDetails?.custom_recipients ?? [];

    const summaryItems = [
        { label: "Campaign", count: campaigns.length, icon: <CampaignOutlinedIcon className={style.archiveErrorSummaryIcon} /> },
        { label: "Workflow", count: workflows.length, icon: <AccountTreeOutlinedIcon className={style.archiveErrorSummaryIcon} /> },
        { label: "Custom Recipients", count: customRecipients.length, icon: <PersonAddAltOutlinedIcon className={style.archiveErrorSummaryIcon} /> },
    ]

    const onClickOfTableCell = (headerObj: HeaderListObjectModel, row: archiveErrorTableRow) => {
        window.open(`${window.location.origin}${row.redirectUrl}`, "_blank", "noopener,noreferrer");
    }

    const tableRowData: archiveErrorTableRow[] = [
        ...campaigns.map((campaign) => ({
            name: campaign.campaign_title,
            type: "Campaign",
            details: campaign.campaign_execution_status_name,
            redirectUrl: `/whatsappcampaign/createnewcampaign?campaign_id=${campaign.campaign_id}`,
        })),
        ...workflows.map((workflow) => ({
            name: workflow.chatbot_name,
            type: "Workflow",
            details: getWorkflowUsageLabel(workflow.usage_type),
            redirectUrl: `/workflow/staticWorkflowDetails?workflow_id=${workflow.chatbot_id}`,
        })),
        ...customRecipients.map((customRecipient) => ({
            name: customRecipient.chatbot_name,
            type: "Custom Recipient",
            details: customRecipient.contact_phone_number,
            redirectUrl: `/channels/viewintegration/whatsapp/phonenumbers/customRecipient?id=${customRecipient?.meta_integration_whatsapp_id}&phonenumberId=${customRecipient?.phone_number_id}`,
        })),
    ];

    const tableHeaderList: HeaderListObjectModel[] = [
        {
            name: "Name",
            alignment: "left",
            flagIcon: false,
            valueKey: "name",
            iconKey: "",
            renderCell: (row: archiveErrorTableRow) => (
                <Typography className={style.archiveErrorNameLink} title={row.name}>
                    {row.name}
                </Typography>
            ),
        },
        { name: "Type", alignment: "left", flagIcon: false, valueKey: "type", iconKey: "" },
        { name: "Details", alignment: "left", flagIcon: false, valueKey: "details", iconKey: "" },
    ];

    return (
        <Grid container>
            <Grid item xs={12}>
                <Grid container direction={"column"} justifyContent={"center"}>
                    
                    {/* Title */}
                    <Grid item textAlign={"center"}>
                        <Typography className={popupStyles.popupTitleTypography}>
                            Unable to Archive List
                        </Typography>
                    </Grid>

                    <Divider className={popupStyles.popupTitleDivider} />

                    <Grid item className={style.archiveErrorPopupBody}>
                        <Grid container className={popupStyles.popupBodyGridContainer} gap={"16px"}>
                            
                            {/* Warning Message */}
                            <Grid item xs={12} className={style.archiveErrorBanner}>
                                <ErrorOutlineIcon className={style.archiveErrorBannerIcon} />
                                <Typography className={style.archiveErrorBannerText}>
                                    This contact list is in use. Remove it from the campaigns, workflows or custom recipients below before archiving it.
                                </Typography>
                            </Grid>

                            {/* Summary */}
                            <Grid item xs={12}>
                                <Grid container gap={"12px"} wrap="nowrap" className={style.archiveErrorSummaryContainer}>
                                    {summaryItems.map((item) => (
                                        <Grid item xs key={item.label} className={style.archiveErrorSummaryCard}>
                                            <div className={style.archiveErrorSummaryIconWrapper}>
                                                {item.icon}
                                            </div>
                                            <Typography className={style.archiveErrorSummaryCount}>
                                                {item.count}
                                            </Typography>
                                            <Typography className={style.archiveErrorSummaryLabel}>
                                                {item.label}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* Table */}
                            <Grid item xs={12} className={`${style.archiveErrorPopupBody} ${style.archiveErrorTableWrapper}`}>
                                <GlobalTable
                                    headerList={tableHeaderList}
                                    rowData={tableRowData}
                                    onCellClick={onClickOfTableCell}
                                    tableHeight={"240px"}
                                    tableContainerMinHeight={"auto"}
                                    flagPagination
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                    
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ArchiveContactErrorPopup;
